import { useTranslation } from '@/hooks/useTranslation';
import { formatDuration, formatVolume, formatDistance } from '@/utils/format';
import { lubricationTypes } from '@/features/traceability/data/lubricationOptions';
import { clampingTypes } from '@/features/traceability/data/clampingOptions';
import { toolHolderTypes } from '@/features/traceability/data/toolHolderOptions';
import type { ComputedStats } from '../utils/computeTraceStats';

export type ToolDataPanelProps = {
  stats: ComputedStats;
};

type Row = { label: string; value: string };

const numberFmt = new Intl.NumberFormat();

function isMeaningful(value: string): boolean {
  return value !== '' && value !== '—' && value !== 'N/A';
}

export function ToolDataPanel({ stats }: ToolDataPanelProps) {
  const { t } = useTranslation();

  const lastTrace = stats.recentTraces[0] ?? null;

  const perfRows: Row[] = [];

  if (stats.totalMachiningSeconds !== 0) {
    const v = formatDuration(stats.totalMachiningSeconds);
    if (isMeaningful(v)) perfRows.push({ label: t('toolView.dataPanel.perf.totalDuration'), value: v });
  }

  if (stats.totalPieces !== 0) {
    perfRows.push({ label: t('toolView.dataPanel.perf.pieces'), value: numberFmt.format(stats.totalPieces) });
  }

  if (stats.totalMachiningSeconds !== 0 && stats.totalPieces !== 0) {
    const v = formatDuration(stats.totalMachiningSeconds / stats.totalPieces);
    if (isMeaningful(v)) perfRows.push({ label: t('toolView.dataPanel.perf.durationPerPiece'), value: v });
  }

  if (stats.totalVolume !== 0) {
    const v = formatVolume(stats.totalVolume);
    if (isMeaningful(v)) perfRows.push({ label: t('toolView.dataPanel.perf.volume'), value: v });
  }

  if (stats.totalDistance !== 0) {
    const v = formatDistance(stats.totalDistance);
    if (isMeaningful(v)) perfRows.push({ label: t('toolView.dataPanel.perf.distance'), value: v });
  }

  if (stats.totalTraces !== 0) {
    perfRows.push({ label: t('toolView.dataPanel.perf.traces'), value: numberFmt.format(stats.totalTraces) });
  }

  const envRows: Row[] = [];

  if (lastTrace) {
    const model = lastTrace.machine?.model;
    if (model) envRows.push({ label: t('toolView.dataPanel.env.machineModel'), value: model });

    if (lastTrace.operation) envRows.push({ label: t('toolView.dataPanel.env.operation'), value: lastTrace.operation });

    if (lastTrace.vc != null) envRows.push({ label: t('toolView.dataPanel.env.vc'), value: `${lastTrace.vc} m/min` });
    if (lastTrace.fz != null) envRows.push({ label: t('toolView.dataPanel.env.fz'), value: `${lastTrace.fz} mm/tooth` });
    if (lastTrace.ap != null) envRows.push({ label: t('toolView.dataPanel.env.ap'), value: `${lastTrace.ap} mm` });
    if (lastTrace.ae != null) envRows.push({ label: t('toolView.dataPanel.env.ae'), value: `${lastTrace.ae} mm` });
    if (lastTrace.malround != null) envRows.push({ label: t('toolView.dataPanel.env.malround'), value: `${lastTrace.malround} µm` });

    const lubLabel = lastTrace.lubType
      ? (lubricationTypes.find((o) => o.value === lastTrace.lubType)?.labelKey ?? null)
      : null;
    if (lubLabel) envRows.push({ label: t('toolView.dataPanel.env.lubType'), value: t(lubLabel) });

    if (lastTrace.lubBrand) envRows.push({ label: t('toolView.dataPanel.env.lubBrand'), value: lastTrace.lubBrand });

    const holderLabel = lastTrace.holderType
      ? (toolHolderTypes.find((o) => o.value === lastTrace.holderType)?.labelKey ?? null)
      : null;
    if (holderLabel) envRows.push({ label: t('toolView.dataPanel.env.holderType'), value: t(holderLabel) });

    const clampLabel = lastTrace.clampingType
      ? (clampingTypes.find((o) => o.value === lastTrace.clampingType)?.labelKey ?? null)
      : null;
    if (clampLabel) envRows.push({ label: t('toolView.dataPanel.env.clampingType'), value: t(clampLabel) });
  }

  if (perfRows.length === 0 && envRows.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-6">
      {perfRows.length > 0 && (
        <Column title={t('toolView.dataPanel.title.performance')} rows={perfRows} />
      )}
      {envRows.length > 0 && (
        <Column title={t('toolView.dataPanel.title.conditions')} rows={envRows} />
      )}
    </div>
  );
}

function Column({ title, rows }: { title: string; rows: Row[] }) {
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      <dl>
        {rows.map((row, i) => (
          <div
            key={row.label}
            className={`py-1.5 ${i < rows.length - 1 ? 'border-b border-border/40' : ''}`}
          >
            <dt className="text-2xs text-muted-foreground">{row.label}</dt>
            <dd className="text-sm font-semibold text-foreground font-mono">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
